import {SafeAreaView, StyleSheet, View} from 'react-native'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import  {type Components} from 'react-markdown'
import ReactMarkdown from 'react-markdown'

import {usePalette} from '#/lib/hooks/usePalette'
import {useWebMediaQueries} from '#/lib/hooks/useWebMediaQueries'
import {s} from '#/lib/styles'
import {isWeb} from '#/platform/detection'
import {useModalControls} from '#/state/modals'
import {Button} from '#/view/com/util/forms/Button'
import {Text} from '#/view/com/util/text/Text'
import {ScrollView} from './util'

export const snapPoints = ['90%']

const createMarkdownComponents = (linkColor: string): Components => ({
  h1: ({children}) => (
    <h1 style={{fontSize: 28, marginBottom: 16, marginTop: 24}}>{children}</h1>
  ),
  h2: ({children}) => (
    <h2 style={{fontSize: 22, marginBottom: 12, marginTop: 20}}>{children}</h2>
  ),
  h3: ({children}) => (
    <h3 style={{fontSize: 18, marginBottom: 10, marginTop: 16}}>{children}</h3>
  ),
  p: ({children}) => <p style={{marginBottom: 12, fontSize: 14}}>{children}</p>,
  ul: ({children}) => (
    <ul style={{marginBottom: 12, paddingLeft: 24}}>{children}</ul>
  ),
  ol: ({children}) => (
    <ol style={{marginBottom: 12, paddingLeft: 24}}>{children}</ol>
  ),
  li: ({children}) => (
    <li style={{marginBottom: 6, fontSize: 14}}>{children}</li>
  ),
  a: ({href, children}) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{color: linkColor, textDecoration: 'underline'}}>
      {children}
    </a>
  ),
  strong: ({children}) => <strong style={{fontWeight: 600}}>{children}</strong>,
})

export function Component({
  title,
  url,
  content,
}: {
  title: string
  url?: string
  content?: string
}) {
  const pal = usePalette('default')
  const {closeModal} = useModalControls()
  const {isMobile} = useWebMediaQueries()
  const {_} = useLingui()

  return (
    <SafeAreaView style={[s.flex1, pal.view]}>
      <View
        style={[
          s.flex1,
          isMobile && {paddingHorizontal: 18},
          styles.container,
        ]}>
        <View style={styles.titleSection}>
          <Text type="title-lg" style={[pal.text, styles.title]}>
            {title}
          </Text>
        </View>

        <View style={styles.iframeContainer}>
          {content ? (
            <ScrollView style={s.flex1}>
              {isWeb ? (
                <div
                  style={{
                    padding: '0 16px',
                    color: String(pal.text.color),
                    lineHeight: 1.6,
                  }}>
                  <ReactMarkdown
                    components={createMarkdownComponents(
                      String(pal.link.color),
                    )}>
                    {content}
                  </ReactMarkdown>
                </div>
              ) : (
                <Text type="lg" style={pal.text}>
                  {content}
                </Text>
              )}
            </ScrollView>
          ) : isWeb ? (
            <iframe
              src={url}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: 8,
              }}
              title={title}
            />
          ) : (
            <ScrollView style={s.flex1}>
              <Text type="lg" style={pal.text}>
                Please visit: {url}
              </Text>
            </ScrollView>
          )}
        </View>

        <View style={[styles.btnContainer, isMobile && {paddingBottom: 40}]}>
          <Button
            testID="closeBtn"
            type="primary"
            onPress={closeModal}
            accessibilityLabel={_(msg`Close`)}
            accessibilityHint={_(msg`Closes the policy document`)}
            label={_(msg`Close`)}
            labelContainerStyle={{justifyContent: 'center', padding: 4}}
            labelStyle={[s.f18]}
          />
        </View>
      </View>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingBottom: isWeb ? 0 : 40,
  },
  titleSection: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    paddingTop: isWeb ? 0 : 4,
    paddingBottom: isWeb ? 14 : 10,
  },
  title: {
    textAlign: 'center',
    fontWeight: '600',
  },
  iframeContainer: {
    flex: 1,
    marginVertical: 10,
  },
  btnContainer: {
    paddingTop: 20,
    gap: 6,
  },
})
